import { useMemo } from 'react';
import { useUser } from '../contexts/UserContext';
import { useOrganization } from '../contexts/OrganizationContext';

export function usePermissions() {
  const { user, isLoading: userLoading } = useUser();
  const { selectedOrganization, isLoading: orgLoading } = useOrganization();

  // Also treat as loading when the user has organizations but selectedOrganization
  // hasn't been set yet. This closes a render-cycle gap: OrganizationContext sets
  // isLoading=false and updates the organizations list in one render, but
  // selectedOrganization is updated in a subsequent useEffect — leaving a brief
  // window where isLoading=false and selectedOrganization=null, which caused
  // PermissionGate to see empty permissions and redirect to /my-apps.
  const hasUserOrgs = (user?.organizations?.length ?? 0) > 0;
  const isLoading = userLoading || orgLoading || (hasUserOrgs && !selectedOrganization);

  const permissions = useMemo(() => {
    if (!user || !selectedOrganization) return new Set<string>();
    const membership = user.organizations.find(
      (m) => m.organizationId === selectedOrganization.id,
    );
    return new Set<string>(membership?.permissions ?? []);
  }, [user, selectedOrganization]);

  return {
    isLoading,
    hasPermission: (perm: string) => permissions.has(perm),
    hasAnyPermission: (perms: string[]) => perms.some((p) => permissions.has(p)),
    permissions,
  };
}
